import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormTemplateQuestion,
  FormTemplateQuestionFieldOptionsValidityHintEnum,
  FormAnswer,
  FormTemplateQuestionFieldsKindEnum,
} from 'graphql-common';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import Typography from '@lib/components/Typography/Typography';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import Table from '@lib/components/Table/Table';
import UserPreviewCell from '@lib/components/Table/commonComponents/UserPreviewCell/UserPreviewCell';
import { getFormattedDate } from '@lib/utils/dateTimeHelpers';
import { dateTimeFormatEU } from '@lib/enums/dateTime';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import useTaskCompletionQueryHook from 'utils/fetch/hooks/useTaskCompletionQueryHook';
import useArrowKeyNavigation from '@lib/hooks/useArrowKeyNavigation';
import { FetchIdsResponse } from '@lib/utils/getAllTableItemsIds';
import mergeTaskQuestionsWithAnswers from './utils/mergeTaskQuestionsWithAnswers';
import getTaskCompletionDetailsTableColumns from './utils/getTaskCompletionDetailsTableColumns';
import useTaskCompletionDetailsActions from './utils/useTaskCompletionDetailsActions';
import {
  FormTemplateQuestionWithAnswers,
  TaskCompletionDetailsTableRow,
} from './types';
import getTableRowClassNameByValidityHint from './utils/getTableRowClassNameByValidityHint';
import styles from './TaskCompletionDetails.module.scss';

type Props = {
  completionId: string | null;
  onChangeCompletionId: (id: string) => void;
  fetchIds?: () => Promise<FetchIdsResponse<unknown>>;
};

export default function TaskCompletionDetailsContent(props: Props) {
  const { completionId, onChangeCompletionId, fetchIds } = props;

  const { t } = useTranslation();

  const {
    allIds,
    onNavigatePrev,
    onNavigateNext,
    canNavigatePrev,
    canNavigateNext,
  } = useTaskCompletionDetailsActions({
    completionId,
    fetchIds,
    onChangeCompletionId,
  });

  const { data, loading, firstLoading, error } = useTaskCompletionQueryHook({
    id: completionId || '',
  });

  const completionValidityHint = data?.validityHint;
  const createdAt = data?.createdAt
    ? getFormattedDate(data.createdAt, dateTimeFormatEU)
    : undefined;
  const version = data?.task?.formTemplateVersion?.version
    ? `v${data?.task?.formTemplateVersion?.version}`
    : undefined;
  const performerFirstName = data?.performer?.firstName || '';
  const performerLastName = data?.performer?.lastName || '';
  const performerName = [performerFirstName, performerLastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const performerAvatar = data?.performer?.avatarAttached?.urls?.small;

  const answers = data?.answers as FormAnswer[];
  const questions = data?.task?.formTemplateVersion
    .questions as FormTemplateQuestion[];

  const mergedQuestions = useMemo<FormTemplateQuestionWithAnswers[]>(
    () =>
      mergeTaskQuestionsWithAnswers(questions, answers, [
        FormTemplateQuestionFieldsKindEnum.Section,
      ]),
    [answers, questions],
  );

  const tableData = useMemo<TaskCompletionDetailsTableRow[]>(
    () =>
      mergedQuestions.flatMap((question) =>
        question.fields.map((field) => ({
          ...field,
          questionName: question.name,
        })),
      ),
    [mergedQuestions],
  );

  const columns = useMemo(
    () => getTaskCompletionDetailsTableColumns({ t }),
    [t],
  );

  useArrowKeyNavigation([
    {
      keys: 'ArrowLeft',
      handler: () => onNavigatePrev?.(),
      disabled: !onNavigatePrev || !canNavigatePrev,
    },
    {
      keys: 'ArrowRight',
      handler: () => onNavigateNext?.(),
      disabled: !onNavigateNext || !canNavigateNext,
    },
  ]);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div>
          {loading && <CircleLoader padding={0} />}
          {!loading && (
            <div className={styles.info}>
              {completionValidityHint ===
                FormTemplateQuestionFieldOptionsValidityHintEnum.Failure && (
                <>
                  <div className={styles.infoItem}>
                    <Chip
                      type={ChipTypes.chipRed}
                      leftIcon="error"
                      size={ChipSizes.small}
                    />
                  </div>
                  <div className={styles.infoItemSeparator} />
                </>
              )}
              {completionValidityHint ===
                FormTemplateQuestionFieldOptionsValidityHintEnum.Success && (
                <>
                  <div className={styles.infoItem}>
                    <Chip
                      type={ChipTypes.chipGreen}
                      leftIcon="check_circle"
                      size={ChipSizes.small}
                    />
                  </div>
                  <div className={styles.infoItemSeparator} />
                </>
              )}
              {completionValidityHint ===
                FormTemplateQuestionFieldOptionsValidityHintEnum.Warning && (
                <>
                  <div className={styles.infoItem}>
                    <Chip
                      type={ChipTypes.chipOrange}
                      leftIcon="warning"
                      size={ChipSizes.small}
                    />
                  </div>
                  <div className={styles.infoItemSeparator} />
                </>
              )}
              <div className={styles.infoItem}>
                <Typography variant="body">{createdAt}</Typography>
              </div>
              <div className={styles.infoItemSeparator} />
              <div className={styles.infoItem}>
                <Chip label={version} type={ChipTypes.chipBlack} />
              </div>
              <div className={styles.infoItemSeparator} />
              <div className={styles.infoItem}>
                <UserPreviewCell
                  name={performerName}
                  avatar={performerAvatar}
                />
              </div>
            </div>
          )}
        </div>
        <div>
          {allIds && allIds.length > 1 ? (
            <div className={styles.nav}>
              <IconButton
                icon="arrow_back"
                type={IconButtonTypes.PrimaryOutlined}
                disabled={!canNavigatePrev}
                onClick={() => (onNavigatePrev ? onNavigatePrev() : undefined)}
              />
              <IconButton
                icon="arrow_forward"
                type={IconButtonTypes.PrimaryOutlined}
                disabled={!canNavigateNext}
                onClick={() => (onNavigateNext ? onNavigateNext() : undefined)}
              />
            </div>
          ) : null}
        </div>
      </div>
      <div className={styles.tableWrapper}>
        {error && <ErrorPage description={error?.message} status={500} />}
        {firstLoading && <CircleLoader />}
        {!error && !firstLoading && (
          <Table<TaskCompletionDetailsTableRow>
            id="task-completion-tasks"
            columns={columns}
            data={tableData}
            totalEntries={tableData.length}
            loading={loading}
            getRowClassName={getTableRowClassNameByValidityHint}
          />
        )}
      </div>
    </div>
  );
}
